import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Plus, Trash2, Loader2, Check, X, AlertTriangle,
  PlayCircle, Wallet, Paperclip, ChevronUp, ChevronDown, Send, Pencil, Mail,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import { useConfirm as useConfirmHook } from "./ConfirmProvider";
import { useAuth } from "../context/AuthContext";
import PaymentStageDocuments from "./PaymentStageDocuments";

const STATUS_META = {
  pending: {
    label: "Pending payment",
    tint: "bg-[#FEF3C7] text-[#92400E]",
    ring: "ring-[#FCD34D]",
    icon: Wallet,
  },
  paid: {
    label: "Paid",
    tint: "bg-[#DCFCE7] text-[#166534]",
    ring: "ring-[#86EFAC]",
    icon: Check,
  },
  waived: {
    label: "Start · Pending payment",
    tint: "bg-[#E0E7FF] text-[#3730A3]",
    ring: "ring-[#A5B4FC]",
    icon: PlayCircle,
  },
};

const fmtMoney = (n) =>
  typeof n === "number" && !Number.isNaN(n)
    ? `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : "—";

export default function PaymentsTab({
  projectId,
  contractValue,
  milestones = [],
  canManage,
  onChange,
}) {
  const [stages, setStages] = useState([]);
  const [stale, setStale] = useState(false);
  const [snapshotContract, setSnapshotContract] = useState(null);
  // Defensive fallback — if the parent forgets to pass a fresh
  // contractValue prop, we read the current value straight from the
  // /payment-stages response so the $/% toggle and stale banner always
  // work.
  const [contractFromApi, setContractFromApi] = useState(null);
  const contractValueEff = Number(contractValue || contractFromApi || 0);
  const [loading, setLoading] = useState(true);
  const [openNew, setOpenNew] = useState(false);
  const [editing, setEditing] = useState(null); // stage object
  const confirm = useConfirmHook();

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/payment-stages`);
      setStages(data.stages || []);
      setStale(!!data.stale);
      setSnapshotContract(data.contract_value_at_stage_creation ?? null);
      setContractFromApi(data.current_contract_value ?? null);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const recomputeFromPercent = async () => {
    const ok = await confirm({
      title: "Recompute payment stages?",
      description: "Every pending stage that was originally set as a percentage will be re-calculated against the current contract value. Paid/waived stages are left alone.",
      confirmText: "Recompute",
    });
    if (!ok) return;
    try {
      const { data } = await api.post(`/projects/${projectId}/payment-stages/recompute-from-percent`);
      toast.success(`Recomputed ${data.updated} payment stage${data.updated === 1 ? "" : "s"}`);
      await load();
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    load();
  }, [projectId]);

  const summary = useMemo(() => {
    // Split "not paid yet" into two buckets:
    //   pending    — ID firm hasn't sent the invoice yet
    //   outstanding — invoice sent to homeowner, awaiting their payment
    let paid = 0,
      pending = 0,
      outstanding = 0,
      waived = 0;
    stages.forEach((s) => {
      if (s.status === "paid") paid += s.amount || 0;
      else if (s.status === "waived") waived += s.amount || 0;
      else if (s.invoice_sent_at) outstanding += s.amount || 0;
      else pending += s.amount || 0;
    });
    const total = paid + pending + outstanding + waived;
    const collected = total > 0 ? Math.round((paid / total) * 100) : 0;
    return { paid, pending, outstanding, waived, total, collected };
  }, [stages]);

  const remove = async (stage) => {
    const ok = await confirm({
      title: "Delete payment stage?",
      description: `“${stage.label}” will be removed.`,
      confirmText: "Delete",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/payment-stages/${stage.stage_id}`);
      toast.success("Deleted");
      await load();
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6" data-testid="payments-tab">
      {stale && canManage && (
        <div
          className="rounded-2xl border border-[#FDE68A] bg-[#FEF3C7] p-4 flex flex-col sm:flex-row sm:items-center gap-3"
          data-testid="payment-stages-stale-banner"
        >
          <div className="flex-1 text-sm text-[#78350F]">
            <span className="font-semibold">Contract value has changed</span> since the payment stages were set{snapshotContract != null ? <> (was <span className="font-mono">${Number(snapshotContract).toLocaleString()}</span>, now <span className="font-mono">${Number(contractValueEff).toLocaleString()}</span>)</> : "."}. Recompute any pending stages that were originally entered as a % of contract.
          </div>
          <button
            type="button"
            onClick={recomputeFromPercent}
            data-testid="payment-stages-recompute-btn"
            className="btn-secondary text-xs whitespace-nowrap"
          >
            Recompute
          </button>
        </div>
      )}
      {/* Summary strip inside the tab */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
        <StatBox label="Contracted" value={fmtMoney(contractValueEff || summary.total)} />
        <StatBox
          label="Paid"
          value={fmtMoney(summary.paid)}
          suffix={`(${summary.collected}%)`}
          accent="text-[#166534]"
        />
        <StatBox label="Pending" value={fmtMoney(summary.pending)} accent="text-[#92400E]" />
        <StatBox
          label="Outstanding"
          value={fmtMoney(summary.outstanding)}
          accent="text-[#9F1239]"
        />
      </div>

      <div className="flex items-center justify-between">
        <h3 className="font-display text-2xl text-[#2C2A29]">Payment schedule</h3>
        {canManage && (
          <button
            className="btn-primary inline-flex items-center gap-2"
            onClick={() => setOpenNew(true)}
            data-testid="new-stage-btn"
          >
            <Plus size={16} /> Add stage
          </button>
        )}
      </div>

      {loading ? (
        <div className="card-soft text-center py-10 text-[#7A7571]">
          <Loader2 className="mx-auto animate-spin" size={20} />
        </div>
      ) : stages.length === 0 ? (
        <div className="card-soft text-center py-14 text-[#7A7571]">
          <Wallet size={32} className="mx-auto mb-3 text-[#7A7571]" />
          <div className="text-[#2C2A29] font-semibold mb-1">No payment stages yet</div>
          <div className="text-sm">
            Add stages like &ldquo;20% non-refundable deposit&rdquo; and link them to milestones. Work on a
            gated milestone can&rsquo;t begin until its payment stage is marked Paid or Waived.
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {stages.map((s, i) => (
            <StageCard
              key={s.stage_id}
              stage={s}
              order={i + 1}
              isFirst={i === 0}
              isLast={i === stages.length - 1}
              milestones={milestones}
              canManage={canManage}
              onUpdate={async () => {
                await load();
                onChange && onChange();
              }}
              onEdit={() => setEditing(s)}
              onDelete={() => remove(s)}
              onMove={async (dir) => {
                // Optimistic local swap so the row stays where the user is
                // looking — no full reload, no scroll jump. Falls back to
                // silent server reconcile on completion.
                setStages((prev) => {
                  const idx = prev.findIndex((x) => x.stage_id === s.stage_id);
                  const swap = dir === "up" ? idx - 1 : idx + 1;
                  if (idx < 0 || swap < 0 || swap >= prev.length) return prev;
                  const next = [...prev];
                  [next[idx], next[swap]] = [next[swap], next[idx]];
                  return next;
                });
                try {
                  await api.post(`/payment-stages/${s.stage_id}/move`, { direction: dir });
                  onChange && onChange();
                } catch (err) {
                  toast.error(formatApiError(err));
                  // Revert on failure
                  await load();
                }
              }}
            />
          ))}
        </div>
      )}

      {(openNew || editing) && (
        <StageDialog
          open={openNew || !!editing}
          onOpenChange={(v) => {
            if (!v) {
              setOpenNew(false);
              setEditing(null);
            }
          }}
          projectId={projectId}
          milestones={milestones}
          stage={editing}
          contractValue={contractValueEff}
          onSaved={async () => {
            await load();
            onChange && onChange();
          }}
        />
      )}
    </div>
  );
}

function StatBox({ label, value, suffix, accent = "text-[#2C2A29]" }) {
  return (
    <div className="card-soft !py-4">
      <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">{label}</div>
      <div className={`font-display text-2xl mt-1 ${accent}`}>
        {value}
        {suffix && (
          <span className="text-sm font-normal ml-1.5 text-[#7A7571]">{suffix}</span>
        )}
      </div>
    </div>
  );
}

function StageCard({ stage, order, isFirst, isLast, milestones, canManage, onUpdate, onEdit, onDelete, onMove }) {
  const meta = STATUS_META[stage.status] || STATUS_META.pending;
  const Icon = meta.icon;
  const { user } = useAuth();
  const linkedIds = stage.linked_milestone_ids || (stage.linked_milestone_id ? [stage.linked_milestone_id] : []);
  const linkedNames = linkedIds
    .map((id) => milestones.find((m) => m.milestone_id === id)?.name)
    .filter(Boolean);
  const [busy, setBusy] = useState(false);
  const [justifyOpen, setJustifyOpen] = useState(false);
  const [justifyText, setJustifyText] = useState(stage.justification || "");
  const [sendingInvoice, setSendingInvoice] = useState(false);
  const invoiceSent = !!stage.invoice_sent_at;
  const invoiceSentDate = invoiceSent ? new Date(stage.invoice_sent_at).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : null;

  const setStatus = async (next, extra = {}) => {
    setBusy(true);
    try {
      await api.patch(`/payment-stages/${stage.stage_id}`, { status: next, ...extra });
      toast.success("Status updated");
      onUpdate && onUpdate();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const sendInvoice = async () => {
    setSendingInvoice(true);
    try {
      await api.post(`/payment-stages/${stage.stage_id}/send-invoice`);
      toast.success(invoiceSent ? "Reminder sent to homeowner" : "Invoice sent to homeowner");
      onUpdate && onUpdate();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSendingInvoice(false); }
  };

  return (
    <div
      className={`card-soft border-2 border-transparent ${busy ? "opacity-70" : ""}`}
      data-testid={`payment-stage-${stage.stage_id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center flex-wrap gap-2 mb-1">
            <span className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">
              Stage {order}
            </span>
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${meta.tint}`}
            >
              <Icon size={12} /> {meta.label}
            </span>
          </div>
          {linkedNames.length > 0 && (
            <div className="flex flex-wrap items-center gap-1 mb-2">
              {linkedNames.map((n) => (
                <span key={n} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#F7F4EC] text-[#7A7571] max-w-full">
                  <span className="truncate">Linked · {n}</span>
                </span>
              ))}
            </div>
          )}
          <div className="text-[#2C2A29] font-semibold text-lg leading-snug break-words">{stage.label}</div>
          <div className="text-2xl font-display text-[#2C2A29] mt-1">{fmtMoney(stage.amount)}</div>
          {stage.due_date && (
            <div className="text-xs text-[#7A7571] mt-1">Due: {stage.due_date}</div>
          )}
          {stage.status === "waived" && stage.justification && (
            <div className="mt-3 rounded-2xl bg-[#EEF2FF] text-[#3730A3] text-sm p-3">
              <div className="text-[10px] uppercase tracking-widest font-bold mb-0.5">Justification</div>
              {stage.justification}
            </div>
          )}
        </div>

        {canManage && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button
              className="btn-ghost !p-2 disabled:opacity-40"
              title="Move up"
              onClick={() => onMove && onMove("up")}
              disabled={isFirst || busy}
              data-testid={`payment-stage-${stage.stage_id}-move-up`}
            >
              <ChevronUp size={16} />
            </button>
            <button
              className="btn-ghost !p-2 disabled:opacity-40"
              title="Move down"
              onClick={() => onMove && onMove("down")}
              disabled={isLast || busy}
              data-testid={`payment-stage-${stage.stage_id}-move-down`}
            >
              <ChevronDown size={16} />
            </button>
            <button
              className="btn-ghost !p-2"
              title="Edit"
              onClick={onEdit}
              data-testid={`payment-stage-${stage.stage_id}-edit`}
            >
              <Pencil size={16} />
            </button>
            <button
              className="btn-ghost !p-2 text-[#9F1239]"
              title="Delete"
              onClick={onDelete}
              data-testid={`payment-stage-${stage.stage_id}-delete`}
            >
              <Trash2 size={16} />
            </button>
          </div>
        )}
      </div>

      {invoiceSent && stage.status !== "paid" && (
        <div className="mt-3 rounded-2xl bg-[#EEF2FF] border border-[#C7D2FE] text-[#3730A3] p-3 flex items-start gap-2 text-sm">
          <Mail size={14} className="mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <div className="font-semibold">Invoice sent to homeowner{invoiceSentDate ? ` on ${invoiceSentDate}` : ""}</div>
            <div className="text-xs mt-0.5 text-[#4C51BF]">
              They&apos;ve been notified and can upload proof of payment from their portal.
            </div>
          </div>
        </div>
      )}

      {/* Multi-file document store shared with the homeowner. */}
      <PaymentStageDocuments
        stageId={stage.stage_id}
        documents={stage.documents || []}
        currentUserId={user?.user_id}
        currentUserRole={user?.role}
        onChange={onUpdate}
      />

      {/* Legacy single-file proof (pre-multi-doc release). Show as a compact
          link if present so nothing is lost. New uploads flow into `documents`. */}
      {stage.proof_uploaded_at && (
        <a
          href={`${api.defaults.baseURL}/payment-stages/${stage.stage_id}/proof`}
          target="_blank"
          rel="noreferrer"
          className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#166534] hover:underline"
        >
          <Paperclip size={12} /> Legacy proof of payment
        </a>
      )}

      {canManage && (
        <div className="mt-4 pt-4 border-t border-[#EBE5D9] flex flex-wrap gap-2">
          {stage.status !== "paid" && (
            <button
              type="button"
              onClick={sendInvoice}
              disabled={busy || sendingInvoice}
              data-testid={`payment-stage-${stage.stage_id}-send-invoice`}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                invoiceSent
                  ? "bg-white border-[#EBE5D9] text-[#2C2A29] hover:bg-[#F7F4EC]"
                  : "bg-[#2C2A29] text-white border-[#2C2A29] hover:opacity-90"
              } ${(busy || sendingInvoice) ? "opacity-60 cursor-not-allowed" : ""}`}
              title={invoiceSent ? "Resend invoice reminder to the homeowner" : "Notify the homeowner and share the invoice"}
            >
              {sendingInvoice ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {invoiceSent ? "Resend invoice" : "Send invoice to homeowner"}
            </button>
          )}
          <StatusButton
            active={stage.status === "paid"}
            onClick={() => setStatus("paid")}
            icon={Check}
            label="Mark as Paid"
            testid={`payment-stage-${stage.stage_id}-status-paid`}
            disabled={busy}
          />
          <StatusButton
            active={stage.status === "waived"}
            onClick={() => {
              setJustifyText(stage.justification || "");
              setJustifyOpen(true);
            }}
            icon={PlayCircle}
            label="Start · Pending payment"
            testid={`payment-stage-${stage.stage_id}-status-waived`}
            disabled={busy}
          />
        </div>
      )}

      <Dialog open={justifyOpen} onOpenChange={setJustifyOpen}>
        <DialogContent className="max-w-lg rounded-3xl">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Start work despite unpaid?</DialogTitle>
            <DialogDescription>
              A justification note is required. This is stored on the project activity log and shown
              to the homeowner.
            </DialogDescription>
          </DialogHeader>
          <textarea
            className="input-soft min-h-[120px]"
            data-testid={`payment-stage-${stage.stage_id}-justify-input`}
            placeholder="e.g. Homeowner approved verbally on WhatsApp; payment due Fri, contractor booked."
            value={justifyText}
            onChange={(e) => setJustifyText(e.target.value)}
          />
          <div className="flex items-center justify-end gap-2 pt-2">
            <button className="btn-secondary" onClick={() => setJustifyOpen(false)}>
              Cancel
            </button>
            <button
              className="btn-primary inline-flex items-center gap-2"
              disabled={!justifyText.trim() || busy}
              data-testid={`payment-stage-${stage.stage_id}-justify-confirm`}
              onClick={async () => {
                await setStatus("waived", { justification: justifyText.trim() });
                setJustifyOpen(false);
              }}
            >
              <AlertTriangle size={16} /> Confirm start
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatusButton({ active, icon: Icon, label, onClick, testid, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
        active
          ? "bg-[#2C2A29] text-white border-[#2C2A29]"
          : "bg-white border-[#EBE5D9] text-[#2C2A29] hover:bg-[#F7F4EC]"
      } ${disabled ? "opacity-60 cursor-not-allowed" : ""}`}
    >
      <Icon size={14} /> {label}
    </button>
  );
}

function StageDialog({ open, onOpenChange, projectId, milestones, stage, contractValue, onSaved }) {
  const isEdit = !!stage;
  const initialIds = stage?.linked_milestone_ids
    || (stage?.linked_milestone_id ? [stage.linked_milestone_id] : []);
  const [form, setForm] = useState({
    label: stage?.label || "",
    amount: stage?.amount ?? "",
    // UX shortcut: user enters a % of contract → frontend auto-fills `amount`.
    // Stored on the backend as `percent_of_contract` so the Payments UI can
    // badge the stage (e.g. "10% of contract") and the recompute flow can
    // update it when contract value drifts.
    mode: stage?.percent_of_contract != null ? "percent" : "dollar",
    percent: stage?.percent_of_contract != null ? String(stage.percent_of_contract) : "",
    linked_milestone_ids: initialIds,
    due_date: stage?.due_date || "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const ids = stage?.linked_milestone_ids
      || (stage?.linked_milestone_id ? [stage.linked_milestone_id] : []);
    setForm({
      label: stage?.label || "",
      amount: stage?.amount ?? "",
      mode: stage?.percent_of_contract != null ? "percent" : "dollar",
      percent: stage?.percent_of_contract != null ? String(stage.percent_of_contract) : "",
      linked_milestone_ids: ids,
      due_date: stage?.due_date || "",
    });
  }, [stage]);

  // Live-computed $ amount when the user is entering a percentage.
  const computedFromPercent = form.mode === "percent" && form.percent !== ""
    ? Math.round((Number(form.percent) / 100) * Number(contractValue || 0) * 100) / 100
    : null;

  const effectiveAmount = form.mode === "percent"
    ? (computedFromPercent ?? 0)
    : Number(form.amount || 0);

  const toggleMs = (id) => {
    setForm((f) => {
      const has = f.linked_milestone_ids.includes(id);
      return {
        ...f,
        linked_milestone_ids: has
          ? f.linked_milestone_ids.filter((x) => x !== id)
          : [...f.linked_milestone_ids, id],
      };
    });
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.label.trim()) return toast.error("Description is required");
    if (form.mode === "percent") {
      if (form.percent === "" || Number(form.percent) <= 0) return toast.error("Percentage must be greater than 0");
      if (!contractValue || contractValue <= 0) return toast.error("Set at least one task line-item so the contract value is > 0 before using a percentage");
    } else if (form.amount === "" || Number(form.amount) <= 0) {
      return toast.error("Amount must be greater than 0");
    }
    setSaving(true);
    try {
      const payload = {
        label: form.label.trim(),
        amount: effectiveAmount,
        percent_of_contract: form.mode === "percent" ? Number(form.percent) : null,
        linked_milestone_ids: form.linked_milestone_ids,
        due_date: form.due_date || null,
      };
      if (isEdit) {
        await api.patch(`/payment-stages/${stage.stage_id}`, payload);
      } else {
        await api.post(`/projects/${projectId}/payment-stages`, payload);
      }
      toast.success(isEdit ? "Updated" : "Payment stage added");
      onSaved && (await onSaved());
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[800px] w-[95vw] rounded-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {isEdit ? "Edit payment stage" : "New payment stage"}
          </DialogTitle>
          <DialogDescription>
            Describe the payment term (e.g. &ldquo;20% non-refundable deposit upon confirmation&rdquo;) and set the
            amount. Link it to a milestone to gate that milestone until the payment is released.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={save} className="space-y-4">
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-1 block">
              Payment terms description <span className="text-[#E07A5F]">*</span>
            </label>
            <textarea
              className="input-soft min-h-[80px]"
              data-testid="payment-stage-label"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              placeholder="20% non-refundable deposit upon confirmation of quotation"
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">
                  Amount <span className="text-[#E07A5F]">*</span>
                </label>
                {/* Compact $ / % toggle. Percentage mode uses the current
                    contract value to compute the $ amount live. */}
                <div className="inline-flex rounded-full border border-[#EBE5D9] bg-white overflow-hidden text-[10px] uppercase tracking-widest font-bold">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, mode: "dollar" })}
                    data-testid="payment-stage-mode-dollar"
                    className={`px-3 py-1 ${form.mode === "dollar" ? "bg-[#2C2A29] text-white" : "text-[#7A7571]"}`}
                  >$</button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, mode: "percent" })}
                    data-testid="payment-stage-mode-percent"
                    className={`px-3 py-1 ${form.mode === "percent" ? "bg-[#2C2A29] text-white" : "text-[#7A7571]"}`}
                  >%</button>
                </div>
              </div>
              {form.mode === "dollar" ? (
                <input
                  type="number" step="any" min="0"
                  className="input-soft"
                  data-testid="payment-stage-amount"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  placeholder="6703.50"
                  required
                />
              ) : (
                <>
                  <div className="rounded-2xl border border-[#EBE5D9] bg-white flex items-stretch">
                    <input
                      type="number" step="0.01" min="0" max="100"
                      className="flex-1 px-3 py-2 bg-transparent focus:outline-none text-[#2C2A29]"
                      data-testid="payment-stage-percent"
                      value={form.percent}
                      onChange={(e) => setForm({ ...form, percent: e.target.value })}
                      placeholder="10"
                      required
                    />
                    <span className="px-3 flex items-center text-[#7A7571] text-sm border-l border-[#EBE5D9] font-mono">%</span>
                  </div>
                  <div className="text-[11px] text-[#7A7571] mt-1" data-testid="payment-stage-computed">
                    {contractValue > 0
                      ? <>= <span className="font-semibold text-[#2C2A29]">${effectiveAmount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</span> of ${Number(contractValue).toLocaleString()} contract</>
                      : "Contract value is 0 — add task line-items first"}
                  </div>
                </>
              )}
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-1 block">
                Due date (optional)
              </label>
              <input
                type="date"
                className="input-soft"
                data-testid="payment-stage-due"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-[#7A7571] mb-1 block">
              Link to milestone(s) — optional, tick as many as apply
            </label>
            {milestones.length === 0 ? (
              <div className="text-sm text-[#7A7571] italic">
                No milestones yet on this project. You can still save this stage and link it later.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-56 overflow-y-auto p-2 rounded-2xl bg-[#FBF7EF] border border-[#EBE5D9]">
                {milestones.map((m) => {
                  const checked = form.linked_milestone_ids.includes(m.milestone_id);
                  return (
                    <label
                      key={m.milestone_id}
                      className={`flex items-center gap-2 px-3 py-2 rounded-xl cursor-pointer border transition-colors ${
                        checked
                          ? "bg-white border-[#2C2A29]"
                          : "bg-white/60 border-transparent hover:bg-white"
                      }`}
                      data-testid={`payment-stage-ms-${m.milestone_id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleMs(m.milestone_id)}
                        className="accent-[#2C2A29]"
                      />
                      <span className="text-sm text-[#2C2A29] truncate">{m.name}</span>
                    </label>
                  );
                })}
              </div>
            )}
            <p className="text-xs text-[#7A7571] mt-1">
              Each linked milestone will show a &ldquo;🔒 Awaiting payment&rdquo; chip until this stage is
              marked Paid or &ldquo;Start · Pending payment&rdquo;.
            </p>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-[#EBE5D9]">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary inline-flex items-center gap-2"
              data-testid="payment-stage-submit"
              disabled={saving}
            >
              {saving ? <Loader2 size={16} className="animate-spin" /> : isEdit ? <Check size={16} /> : <Plus size={16} />}
              {isEdit ? "Save changes" : "Add stage"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/* ------------ Rollup card (used above the tabs) ------------ */
export function PaymentRollupCard({ stages = [], contractValue, onOpenTab }) {
  if (stages.length === 0) return null;

  // Split "not paid yet" into two buckets — pending (invoice not yet sent
  // to homeowner) and outstanding (invoice sent, awaiting payment). Matches
  // the Payments tab tiles so the numbers line up across the app.
  let paid = 0,
    pending = 0,
    outstanding = 0,
    waived = 0;
  stages.forEach((s) => {
    if (s.status === "paid") paid += s.amount || 0;
    else if (s.status === "waived") waived += s.amount || 0;
    else if (s.invoice_sent_at) outstanding += s.amount || 0;
    else pending += s.amount || 0;
  });
  const total = paid + pending + outstanding + waived;
  const collected = total > 0 ? Math.round((paid / total) * 100) : 0;
  const paidCount = stages.filter((s) => s.status === "paid").length;
  const invoiceSentCount = stages.filter(
    (s) => s.invoice_sent_at && s.status !== "paid"
  ).length;
  const unpaidCount = stages.filter((s) => s.status !== "paid").length;

  // Tint: soft warm cream while anything is outstanding, back to neutral once fully paid.
  const cardTint = unpaidCount > 0
    ? "!bg-[#FFF8E3] border-l-4 !border-l-[#F59E0B]"
    : "!bg-gradient-to-br !from-[#FBF8F1] !to-[#F7F4EC]";

  return (
    <div
      className={`card-soft mb-6 cursor-pointer hover:!shadow-lg transition-all ${cardTint}`}
      onClick={onOpenTab}
      data-testid="payment-rollup-card"
    >
      <div className="flex flex-wrap items-center gap-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-[#2C2A29] text-white flex items-center justify-center">
            <Wallet size={18} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">Payments</div>
            <div className="font-display text-lg text-[#2C2A29]">
              {collected}% collected
              {unpaidCount === 0 && total > 0 && (
                <span className="ml-2 text-xs text-[#166534] font-semibold uppercase tracking-widest">✓ fully paid</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-5 text-sm">
          <RollupPill label="Paid" value={fmtMoney(paid)} accent="text-[#166534]" />
          <RollupPill label="Pending" value={fmtMoney(pending)} accent="text-[#92400E]" />
          <RollupPill label="Outstanding" value={fmtMoney(outstanding)} accent="text-[#9F1239]" />
          {waived > 0 && (
            <RollupPill label="Started (unpaid)" value={fmtMoney(waived)} accent="text-[#3730A3]" />
          )}
          <RollupPill label="Contracted" value={fmtMoney(contractValue || total)} />
          <div className="ml-auto flex flex-wrap items-center gap-2">
            {invoiceSentCount > 0 && (
              <span
                className="text-xs px-2.5 py-1 rounded-full bg-white/70 text-[#3730A3] font-semibold border border-[#A5B4FC]/60 inline-flex items-center gap-1"
                data-testid="payment-pill-invoices-sent"
              >
                <Send size={11} />
                {invoiceSentCount} Invoice{invoiceSentCount === 1 ? "" : "s"} sent
              </span>
            )}
            {paidCount > 0 && (
              <span
                className="text-xs px-2.5 py-1 rounded-full bg-white/70 text-[#166534] font-semibold border border-[#86EFAC]/70 inline-flex items-center gap-1"
                data-testid="payment-pill-paid"
              >
                <Check size={11} />
                {paidCount} Paid
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function RollupPill({ label, value, accent = "text-[#2C2A29]" }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-[#7A7571]">{label}</div>
      <div className={`font-semibold ${accent}`}>{value}</div>
    </div>
  );
}
